
import React, { useMemo, useState } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from "recharts";
import { formatNumber } from "../../../../utils/HelperFuncitons";

export default function PaymentChart({ initialSource = "ride", initialPeriod = "month" }) {
    const [chartSource, setChartSource] = useState(initialSource);
    const [period, setPeriod] = useState(initialPeriod);

    // generate dummy data per period (day, week, month, year) and per source
    const chartData = useMemo(() => {
        const now = new Date();

        const makeNumber = (i, base, amp) => Math.max(0, Math.round(base + Math.sin(i / 2) * amp + ((i % 3) - 1) * 80));

        const makeLabelsForDays = (days) => {
            const res = [];
            for (let i = days - 1; i >= 0; i--) {
                const d = new Date(now);
                d.setDate(now.getDate() - i);
                res.push(d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
            }
            return res;
        };

        const makeLabelsForHours = () => {
            return Array.from({ length: 24 }).map((_, i) => {
                const hour = i % 12 === 0 ? 12 : i % 12;
                const suffix = i < 12 ? 'AM' : 'PM';
                return `${hour} ${suffix}`;
            });
        };

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const buildSeries = (source) => {
            const isRide = source === 'ride';
            const base = isRide ? 2200 : 1500;
            const amp = isRide ? 1200 : 1000;

            if (period === 'day') {
                const labels = makeLabelsForHours();
                return labels.map((label, i) => ({ label, primary: makeNumber(i, base / 3, amp / 3), secondary: Math.max(0, makeNumber(i + 2, base / 4, amp / 4)) }));
            }

            if (period === 'week') {
                const labels = makeLabelsForDays(7);
                return labels.map((label, i) => ({ label, primary: makeNumber(i, base, amp), secondary: Math.max(0, makeNumber(i + 1, base * 0.8, amp * 0.9)) }));
            }

            if (period === 'month') {
                const labels = makeLabelsForDays(30);
                return labels.map((label, i) => ({ label, primary: makeNumber(i, base, amp), secondary: Math.max(0, makeNumber(i + 3, base * 0.9, amp * 0.8)) }));
            }

            // year
            return months.map((m, i) => ({ label: m, primary: makeNumber(i, base * 1.5, amp * 1.2), secondary: Math.max(0, makeNumber(i + 2, base, amp)) }));
        };

        return buildSeries(chartSource);
    }, [chartSource, period]);

    const formatTick = (value) => {
        if (value >= 1000) return `${Math.round(value / 1000)}k`;
        return value;
    };

    const tabContainerStyle = {
        display: "flex",
        alignItems: "center",
        background: "#f8f8ff",
        padding: "6px",
        borderRadius: "8px",
        gap: "4px",
    };

    const pillStyle = (active) => ({
        padding: "6px 12px",
        borderRadius: "8px",
        cursor: "pointer",
        background: active ? "#0064CF" : "transparent",
        color: active ? "#fff" : "#9291A5",
        border: "none",
        fontSize: "14px",
        fontWeight: 500,
        transition: "all 0.2s ease",
    });

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                    <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Total Payment</h4>
                    <div style={{ marginTop: 8 }}>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <select
                                value={chartSource}
                                onChange={(e) => setChartSource(e.target.value)}
                                className="form-select"
                                style={{ width: 80, padding: '3px 5px', borderRadius: 4, border: '1.5px solid #2B4BFF', background: '#fff', color: '#1f2937', fontSize: 13, appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                            >
                                <option value="ride">Ride</option>
                                <option value="restaurant">Restaurant</option>
                            </select>
                            <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#2B4BFF' }}>
                                <svg width="10" height="6" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1l5 5 5-5" stroke="#2B4BFF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={tabContainerStyle}>
                    {["Day", "Week", "Month", "Year"].map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPeriod(p.toLowerCase())}
                            style={pillStyle(period === p.toLowerCase())}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#eef2f7" vertical={false} />
                        <XAxis dataKey="label" tick={{ fill: "#6b7280", fontSize: 11 }} />
                        <YAxis tickFormatter={formatTick} tick={{ fill: "#6b7280", fontSize: 12 }} />
                        <Tooltip formatter={(value) => formatNumber(value || 0)} />
                        <ReferenceLine y={2400} stroke="#2B4BFF" strokeDasharray="3 6" strokeOpacity={0.25} />
                        <Line
                            type="linear"
                            dataKey="primary"
                            stroke="#2B4BFF"
                            strokeWidth={3}
                            dot={{ r: 6, fill: '#2B4BFF', stroke: "#fff", strokeWidth: 2 }}
                            activeDot={{ r: 8, fill: '#2B4BFF' }}
                            animationDuration={500}
                        />
                        <Line
                            type="linear"
                            dataKey="secondary"
                            stroke="#C089FF"
                            strokeWidth={3}
                            dot={{ r: 6, fill: '#C089FF', stroke: "#fff", strokeWidth: 2 }}
                            activeDot={{ r: 8, fill: '#C089FF' }}
                            animationDuration={500}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}